/**
 * Website Content Service
 *
 * Loads CMS content from Firestore and merges with static defaults.
 * Falls back entirely to static data if CMS has no data.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { WebsiteContent } from '../types/websiteCms.types';

// Static defaults
import {
  NAV_ITEMS, SOCIAL_LINKS, CONTACT_INFO, APP_LINKS, EXTERNAL_TOOLS,
  FOOTER_COLUMNS, BUSINESS_HOURS, HERO_SLIDES, SERVICES_GRID,
  SERVICES_BUTTONS, TOOLS_GRID, BEGINNERS_VIDEOS, REGION_CARDS, CTA_CARDS,
} from '../data/siteContent';

import {
  ABOUT_PAGE, HOME_PAGE, CAREERS_PAGE, CONTACT_PAGE, CONSULTATION_PAGE,
  SERVICES_PAGE, BEGINNERS_PAGE, TOOLS_PAGE, PROPERTY_PAGE, SERVICE_PAGES,
} from '../data/pageContent';

const CMS_COLLECTION = 'websiteContent';
const DOC_IDS = ['global', 'heroSlides', 'videos', 'pages', 'servicePages', 'gridsAndCards'] as const;

/** Extract YouTube video ID from any URL format or raw ID */
function extractYouTubeId(input: string): string {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed);
    if (url.searchParams.has('v')) return url.searchParams.get('v') || trimmed;
    const m = url.pathname.match(/^\/(embed\/|v\/|shorts\/)?([A-Za-z0-9_-]{11})/);
    if (m) return m[2];
  } catch { /* not a URL */ }
  return trimmed;
}

/** Sanitize all video IDs in the loaded content */
function sanitizeVideoIds(content: WebsiteContent): void {
  // Beginner videos
  content.beginnerVideos.forEach(v => { v.videoId = extractYouTubeId(v.videoId); });
  // Page videos
  for (const key of Object.keys(content.pageVideos) as (keyof typeof content.pageVideos)[]) {
    if (content.pageVideos[key]) {
      content.pageVideos[key] = extractYouTubeId(content.pageVideos[key]!);
    }
  }
}

function getStaticDefaults(): WebsiteContent {
  return {
    contactInfo: { ...CONTACT_INFO },
    socialLinks: { ...SOCIAL_LINKS },
    appLinks: { ...APP_LINKS },
    externalTools: { ...EXTERNAL_TOOLS },
    businessHours: { ...BUSINESS_HOURS },
    footerColumns: {
      company: FOOTER_COLUMNS.company.map(c => ({ ...c })),
      freeTools: FOOTER_COLUMNS.freeTools.map(c => ({
        label: c.label,
        path: c.path,
        ...('external' in c ? { external: true } : {}),
      })),
      services: FOOTER_COLUMNS.services.map(c => ({ ...c })),
    },
    // Site-wide switches — the investment-summary form ships DARK.
    flags: { investmentSummaryFormEnabled: false },
    tracking: {
      googleAnalyticsIds: '',
      facebookPixelId: '',
      googleTagManagerId: '',
      trafficGuardPropertyId: '',
      googleAdsConversionId: '',
      googleAdsLeadConversionLabel: '',
      customHeadScripts: '',
    },
    seo: {
      // No siteTitle/siteDescription defaults: every page owns its own
      // title/description via SEOHead. A site-wide value here (or in the CMS)
      // would emit competing tags that only React mount order keeps from
      // overriding the per-page ones \u2014 retired 2026-09-08.
      siteTitle: '',
      siteDescription: '',
      ogImageUrl: '',
      siteUrl: 'https://propinvest.co.za/',
      fbAppId: '',
      jsonLd: '',
    },
    heroSlides: HERO_SLIDES.map(s => ({ ...s, enabled: true })),
    beginnerVideos: BEGINNERS_VIDEOS.map(v => ({ ...v })),
    pageVideos: {},
    pageVideoEntries: {},
    pages: {
      about: { ...ABOUT_PAGE, pillars: [...ABOUT_PAGE.pillars] },
      home: { whyInvest: { ...HOME_PAGE.whyInvest }, allServices: { ...HOME_PAGE.allServices } },
      careers: { ...CAREERS_PAGE, questions: [...CAREERS_PAGE.questions] },
      contact: { getInTouch: { ...CONTACT_PAGE.getInTouch }, formHeading: CONTACT_PAGE.formHeading, hoursHeading: CONTACT_PAGE.hoursHeading },
      consultation: { ...CONSULTATION_PAGE },
      services: { ...SERVICES_PAGE },
      beginners: { ...BEGINNERS_PAGE },
      tools: { ...TOOLS_PAGE },
      property: { ...PROPERTY_PAGE },
    },
    servicePages: {
      refinance: { ...SERVICE_PAGES.refinance, benefits: [...SERVICE_PAGES.refinance.benefits] },
      resales: { ...SERVICE_PAGES.resales, benefits: [...SERVICE_PAGES.resales.benefits] },
      rentals: { ...SERVICE_PAGES.rentals, benefits: [...SERVICE_PAGES.rentals.benefits] },
      trustee: { ...SERVICE_PAGES.trustee, benefits: [...SERVICE_PAGES.trustee.benefits] },
      insurance: { ...SERVICE_PAGES.insurance, benefits: [...SERVICE_PAGES.insurance.benefits] },
      capitalInvestment: { ...SERVICE_PAGES.capitalInvestment, benefits: [...SERVICE_PAGES.capitalInvestment.benefits] },
      trustCreation: { ...SERVICE_PAGES.trustCreation, benefits: [...SERVICE_PAGES.trustCreation.benefits] },
      companyRegistration: { ...SERVICE_PAGES.companyRegistration, benefits: [...SERVICE_PAGES.companyRegistration.benefits] },
      wills: { ...SERVICE_PAGES.wills, benefits: [...SERVICE_PAGES.wills.benefits] },
    },
    navItems: NAV_ITEMS.map((item, i) => ({ label: item.label, path: item.path, order: i })),
    servicesGrid: SERVICES_GRID.map(s => ({ ...s, enabled: true })),
    servicesButtons: SERVICES_BUTTONS.map(s => ({ ...s, enabled: true })),
    toolsGrid: TOOLS_GRID.map(t => ({ ...t, enabled: true })),
    regionCards: REGION_CARDS.map(r => ({ ...r, enabled: true })),
    ctaCards: CTA_CARDS.map(c => ({ ...c, enabled: true })),
  };
}

// Deep merge: overlay CMS data on top of defaults
function deepMerge<T>(defaults: T, overlay: Partial<T> | null | undefined): T {
  if (!overlay) return defaults;
  const result = { ...defaults };
  for (const key of Object.keys(overlay) as (keyof T)[]) {
    const val = overlay[key];
    if (val === undefined || val === null) continue;
    if (Array.isArray(val)) {
      (result as Record<string, unknown>)[key as string] = val;
    } else if (typeof val === 'object' && !Array.isArray(val)) {
      (result as Record<string, unknown>)[key as string] = deepMerge(
        (defaults as Record<string, unknown>)[key as string] || {},
        val as Record<string, unknown>
      );
    } else {
      (result as Record<string, unknown>)[key as string] = val;
    }
  }
  return result;
}

/**
 * Load all CMS content from Firestore in parallel, merge with static defaults.
 */
export async function loadWebsiteContent(): Promise<WebsiteContent> {
  const defaults = getStaticDefaults();

  try {
    const snapshots = await Promise.all(
      DOC_IDS.map(id => getDoc(doc(db, CMS_COLLECTION, id)))
    );

    const [globalSnap, heroSnap, videosSnap, pagesSnap, servicePagesSnap, gridsSnap] = snapshots;

    // Merge global
    if (globalSnap.exists()) {
      const g = globalSnap.data();
      if (g.contactInfo) defaults.contactInfo = deepMerge(defaults.contactInfo, g.contactInfo);
      if (g.socialLinks) defaults.socialLinks = deepMerge(defaults.socialLinks, g.socialLinks);
      if (g.appLinks) defaults.appLinks = deepMerge(defaults.appLinks, g.appLinks);
      if (g.externalTools) defaults.externalTools = deepMerge(defaults.externalTools, g.externalTools);
      if (g.businessHours) defaults.businessHours = deepMerge(defaults.businessHours, g.businessHours);
      if (g.footerColumns) defaults.footerColumns = deepMerge(defaults.footerColumns, g.footerColumns);
      if (g.tracking) defaults.tracking = deepMerge(defaults.tracking, g.tracking);
      if (g.flags) defaults.flags = deepMerge(defaults.flags, g.flags);
      if (g.seo) defaults.seo = deepMerge(defaults.seo, g.seo);
    }

    // Merge hero slides
    if (heroSnap.exists()) {
      const h = heroSnap.data();
      if (h.slides?.length) {
        defaults.heroSlides = h.slides.filter((s: { enabled?: boolean }) => s.enabled !== false);
      }
      if (h.defaultDurationSeconds) defaults.heroSlideDefaultDuration = h.defaultDurationSeconds;
    }

    // Merge videos
    if (videosSnap.exists()) {
      const v = videosSnap.data();
      if (v.beginnersSeries?.length) defaults.beginnerVideos = v.beginnersSeries;
      if (v.pageVideos) defaults.pageVideos = { ...defaults.pageVideos, ...v.pageVideos };
      if (v.pageVideoEntries) defaults.pageVideoEntries = { ...defaults.pageVideoEntries, ...v.pageVideoEntries };
    }

    // Merge pages
    if (pagesSnap.exists()) {
      defaults.pages = deepMerge(defaults.pages, pagesSnap.data() as Partial<typeof defaults.pages>);
    }

    // Merge service pages
    if (servicePagesSnap.exists()) {
      defaults.servicePages = deepMerge(defaults.servicePages, servicePagesSnap.data() as Partial<typeof defaults.servicePages>);
    }

    // Merge grids & cards
    if (gridsSnap.exists()) {
      const gc = gridsSnap.data();
      if (gc.navItems?.length) defaults.navItems = gc.navItems;
      if (gc.servicesGrid?.length) defaults.servicesGrid = gc.servicesGrid;
      if (gc.servicesButtons?.length) defaults.servicesButtons = gc.servicesButtons;
      if (gc.toolsGrid?.length) defaults.toolsGrid = gc.toolsGrid;
      if (gc.regionCards?.length) defaults.regionCards = gc.regionCards;
      if (gc.ctaCards?.length) defaults.ctaCards = gc.ctaCards;
      // gc.beginnerVideos is deliberately IGNORED: the retired Cards & Grids
      // tab used to write a downgraded copy here (title+videoId only) that
      // clobbered the Videos tab's full series (uploads, thumbnails). The
      // Videos tab (videos.beginnersSeries) is the single source of truth.
    }
  } catch (err) {
    console.warn('[CMS] Failed to load CMS content, using static defaults:', err);
  }

  // Clean any full YouTube URLs that were saved as video IDs
  sanitizeVideoIds(defaults);

  return defaults;
}
